"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";

import useLoginModal from "@/app/hooks/useLoginModal";
import { newChatPath } from "@/app/utils/chatPath";
import {
  SafeAvailabilityRule,
  SafeListing,
  SafeReviews,
  SafeUser,
} from "@/app/types";

import Container from "@/app/components/Container";
import { categories } from "@/app/components/navbar/Categories";
import ListingHead from "@/app/components/listings/ListingHead";
import ListingInfo from "@/app/components/listings/ListingInfo";
import ListingReservation from "@/app/components/listings/ListingReservation";
import { ExchangeCurrency } from "@prisma/client";
import ListingCard from "@/app/components/listings/ListingCard";
import useCurrencyStore from "@/app/hooks/useCurrencyStore";
import ReviewsSection from "@/app/components/ReviewsSection";
import ResponsiveGrid from "@/app/components/listings/ResponsiveGridListings";
import convertPrice from "@/app/utils/CurrencyConverter";
import BeginnerAlternatives from "@/app/components/booking/BeginnerAlternatives";
import type { BeginnerAlternative } from "@/app/libs/beginnerAlternatives";
import MobileBookBar from "@/app/components/listings/MobileBookBar";
import useCurrencies from "@/app/hooks/useCurrencies";
import formatPrice from "@/app/utils/formatPrice";

const RESERVATION_ANCHOR_ID = "reservation-start";


interface ListingClientProps {
  listing: SafeListing;
  currentUser?: SafeUser | null;
  listings: SafeListing[];
  rates: ExchangeCurrency | null;
  availabilityRules: SafeAvailabilityRule[];
  currentMonth: number;
  currentYear: number;
  reviews: SafeReviews[];
  totalReviews: number;
  /** Beginner listing to offer when this one needs a certification the visitor does not have. */
  beginnerAlternatives?: BeginnerAlternative[];
}

const ListingClient: React.FC<ListingClientProps> = ({
  listing,
  currentUser,
  listings,
  rates,
  availabilityRules = [],
  reviews,
  totalReviews,
  currentMonth,
  currentYear,
  beginnerAlternatives = [],
}) => {
  const loginModal = useLoginModal();
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [convertedPrices, setConvertedPrices] = useState<Record<string, number>>({});

  const currencyStore = useCurrencyStore();
  const selectedCurrency = currencyStore.selectedCurrency;

  const filteredListings = listings.filter((item) => item.id !== listing.id);

  const isAdmin = currentUser?.role === "ADMIN";
  const bookingDisabled =
    isLoading || (!isAdmin && listing.isActive !== true);

  const category = useMemo(() => {
    return categories.find((items) => items.label === listing.category);
  }, [listing.category]);

  const convertAllPrices = useCallback(async () => {
    const pricesToConvert = {
      price: listing.price,
      priceExtraDiver: listing.priceExtraDiver ?? 0,
      priceExtraSnorkeler: listing.priceExtraSnorkeler ?? 0,
      priceNitrox: listing.priceNitrox ?? 0,
      price15L: listing.price15L ?? 0,
      priceFullSet: listing.priceFullSet ?? 0,
      priceComputer: listing.priceComputer ?? 0,
      priceTorch: listing.priceTorch ?? 0,
      priceAdapter: listing.priceAdapter ?? 0,
      priceBCD: listing.priceBCD ?? 0,
      priceReg: listing.priceReg ?? 0,
      priceWetsuit: listing.priceWetsuit ?? 0,
      priceMask: listing.priceMask ?? 0,
      priceFins: listing.priceFins ?? 0,
      priceWeights: listing.priceWeights ?? 0,
      pricePrivateGuide: listing.pricePrivateGuide ?? 0,
      priceFees: listing.priceFees ?? 0,
    };

    let baseCurrency = selectedCurrency?.code || currentUser?.currency;
    if (!baseCurrency) {
      baseCurrency = "USD";
    }

    if (baseCurrency === "USD" && listing.currency === "USD") {
      setConvertedPrices(pricesToConvert);
      return;
    }


    const convertedPrices = await Promise.all(
      Object.entries(pricesToConvert).map(async ([key, price]) => {
        const newPrice = await convertPrice(price, listing.currency || "", baseCurrency, rates);
        return [key, parseFloat(newPrice.toFixed(0))];
      })
    );

    setConvertedPrices(Object.fromEntries(convertedPrices));
  }, [listing, selectedCurrency, currentUser, rates]);

  useEffect(() => {
    convertAllPrices();
  }, [convertAllPrices]);

  // Same rating rule as ListingInfo: TripAdvisor only when the visitor can
  // verify it (the center has a TripAdvisor location id).
  const center = listing.diveCenter;
  const buRating = center.averageRating ?? 0;
  const headRating =
    buRating > 0
      ? buRating
      : center.tripAdvisorLocationId
        ? center.averageRatingTripAdvisor ?? 0
        : 0;
  const headReviews =
    buRating > 0
      ? center.totalReviews ?? 0
      : center.tripAdvisorLocationId
        ? center.totalReviewsTripAdvisor ?? 0
        : 0;

  // Mirrors the per-diver price of the reservation panel (discount applied,
  // mandatory fees included).
  const { getByCode } = useCurrencies();
  const userCurrencySymbol =
    getByCode(currentUser?.currency as string)?.symbol || "$";
  const barSymbol =
    currentUser || !selectedCurrency?.symbol
      ? userCurrencySymbol
      : selectedCurrency.symbol;
  const barPrice = convertedPrices.price;
  const barDiscount = listing.discountTotal ?? 0;
  const barFee = listing.notIncluded?.includes("Fees")
    ? convertedPrices.priceFees ?? 0
    : 0;
  const barPriceLabel =
    typeof barPrice === "number"
      ? `${barSymbol} ${formatPrice(barPrice - (barPrice * barDiscount) / 100 + barFee)}`
      : null;

  // Opens the chat as a draft: nothing is created until the first message.
  const onContactDiveCenter = () => {
    if (!currentUser) {
      loginModal.onOpen();
      return;
    }
    router.push(newChatPath("listing", listing.id));
  };

  return (
    <Container>
      <div
        className="
          max-w-screen-lg 
          mx-auto
        "
      >
        <div className="flex flex-col gap-2 mt-4 sm:mt-12">
          <ListingHead
            title={listing.title}
            imageSrc={listing.imageSrc || ""}
            locationValue={listing.diveCenter.country || ""}
            id={listing.id}
            currentUser={currentUser}
            category={listing.category}
            minCertification={listing.minCertification}
            operatorName={listing.diveCenter.user.name}
            rating={headRating}
            reviewsNumber={headReviews}
          />
          <div
            className="
              grid 
              grid-cols-1 
              md:grid-cols-7 
              md:gap-10 
              mt-6
            "
          >
            <ListingInfo
              diveCenterId={listing.diveCenterId || ""}
              userName={listing.diveCenter.user.name}
              category={category}
              description={listing.description}
              locationValue={listing.diveCenter.country || ""}
              listing={listing}
            />
            <div
              id={RESERVATION_ANCHOR_ID}
              className="
                order-first 
                mb-10 
                md:order-last 
                md:col-span-3
              "
            >
              {listing.isActive !== true && !isAdmin && (
                <div
                  className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                  role="status"
                >
                  This listing is not accepting new bookings at the moment.
                </div>
              )}
              <ListingReservation
                currentUser={currentUser}
                listing={listing}
                price={convertedPrices.price}
                priceExtraDiver={convertedPrices.priceExtraDiver}
                priceExtraSnorkeler={convertedPrices.priceExtraSnorkeler}
                priceNitrox={convertedPrices.priceNitrox}
                price15L={convertedPrices.price15L}
                priceFullSet={convertedPrices.priceFullSet}
                priceComputer={convertedPrices.priceComputer}
                priceTorch={convertedPrices.priceTorch}
                priceAdapter={convertedPrices.priceAdapter}
                priceBCD={convertedPrices.priceBCD}
                priceReg={convertedPrices.priceReg}
                priceWetsuit={convertedPrices.priceWetsuit}
                priceMask={convertedPrices.priceMask}
                priceFins={convertedPrices.priceFins}
                priceWeights={convertedPrices.priceWeights}
                pricePrivateGuide={convertedPrices.pricePrivateGuide}
                priceFees={convertedPrices.priceFees}
                disabled={bookingDisabled}
                selectedCurrency={selectedCurrency}
                discountTotal={listing.discountTotal ?? 0}
                discountWeekly={listing.discountWeekly ?? 0}
                rates={rates}
                availabilityRules={availabilityRules}
                initialMonth={currentMonth}
                initialYear={currentYear}
              />
              {/* Beginner option right under Reserve, before they book the wrong thing. */}
              {beginnerAlternatives.length > 0 && (
                <BeginnerAlternatives
                  alternatives={beginnerAlternatives}
                  diveCenterId={listing.diveCenterId}
                  currentUser={currentUser}
                  rates={rates}
                  heading="Not certified yet?"
                  prominent
                />
              )}
            </div>
          </div>
        </div>

        <div className="font-bold mt-10">
          {!currentUser || currentUser.userType === "Diver" ? (
            <div
              className="
            top-3
            right-3
          "
            >
              <button onClick={onContactDiveCenter} className="font-bold text-lg border-[1px] border-black bg-white text-black px-4 py-2 rounded-lg" >Contact dive center</button>
            </div>
          ) : null}
        </div>

        <div className="font-bold mt-12">CANCELLATION POLICY</div>
        <div className="text-neutral-600 text-md pt-2">
          {listing.isNonRefundable
            ? "This experience is non-refundable. You can cancel your booking, but no refund will be issued."
            : "Full refunds will be issued for cancellations made at least one week prior to the activity."}
        </div>

        <div className="font-bold mt-12">
          OTHER COURSES FROM THIS DIVE CENTER
        </div>
        {filteredListings.length === 0 ? (
          <div className="mt-3 text-md text-gray-500">
            This center haven't uploaded other courses yet
          </div>
        ) : (
          <ResponsiveGrid
            items={filteredListings}
            itemsPerRow={2}
            maxRowsMobile={1}
          >
            {(listing: any) => (
              <ListingCard
                key={listing.id}
                listing={listing}
                actionId={listing.id}
                currentUser={currentUser}
                diveCenter={listing.diveCenter}
                rates={rates}
              />
            )}
          </ResponsiveGrid>
        )}

        <ReviewsSection
          reviews={reviews}
          totalReviews={totalReviews}
          diveCenterName={listing.diveCenter.user.name}
          diveCenterRating={listing.diveCenter.averageRating}
          listingId={listing.id}
          tripAdvisorLocationId={listing.diveCenter.tripAdvisorLocationId}
          averageRatingTripAdvisor={listing.diveCenter.averageRatingTripAdvisor}
          totalReviewsTripAdvisor={listing.diveCenter.totalReviewsTripAdvisor}
        />
      </div>
      <MobileBookBar
        targetId={RESERVATION_ANCHOR_ID}
        priceLabel={barPriceLabel}
        disabled={bookingDisabled}
      />
    </Container>
  );
};

export default ListingClient;
